import {
  CircularProgress
} from '@mui/material';
import {
  debounce
} from 'lodash';
import React from 'react';
import {
  Arr
} from '../../../lib/Array.lib';
import {
  NOT,
  YES
} from '../../../lib/Boolean.lib';
import {
  ID__ATS_TYPE__ASHBY_API
} from '../../../lib/Definition';
import {
  Fun
} from '../../../lib/Function.lib';
import useState from '../../../lib/hooks/useState.hook';
import {
  getPersonName,
  Obj
} from '../../../lib/Object.lib';
import Ashby from '../../../lib/services/ATS/Ashby.lib';
import getAtsContextProperty from '../../../lib/services/ATS/getAtsContextProperty.fun';
import getAtsContextUpdated from '../../../lib/services/ATS/getAtsContextUpdated.fun';
import {
  joinClassName
} from '../../Layout/Libraries/Theme.lib';
import Box from '../../Layout/Wrappers/Box';
import Fieldset from '../../Layout/Wrappers/Fieldset';
import Icon from '../../Layout/Wrappers/Icon';
import Menu from '../../Layout/Wrappers/Menu';
import Once from '../../Layout/Wrappers/Once';
import TextField from '../../Layout/Wrappers/TextField';

const VALUE__ATS__USER__EMAIL = 'recruiter@10by10.io';

export default function SubSectionAtsSettingsAshbyAdmin({
  acl = true,
  entity = {},
  onChange = Fun,
  ...props
}) {

  const [state, updateState] = useState();
  const entityRef = React.useRef(entity);
  React.useEffect(() => {
    entityRef.current = entity;
  }, [entity]);
  if (NOT(acl)) { return null; }

  const atsTypeId = ID__ATS_TYPE__ASHBY_API;

  // apiKey
  const VALUE__ATS__API_KEY = getAtsContextProperty({
    entity,
    atsTypeId,
    property: 'apiKey'
  });

  // sourceId
  const VALUE__ATS__SOURCE_ID = getAtsContextProperty({
    entity,
    atsTypeId,
    property: 'sourceId'
  });

  // userId
  const VALUE__ATS__USER__ID = getAtsContextProperty({
    entity,
    atsTypeId,
    property: 'userId'
  });

  // userName
  const VALUE__ATS__USER__NAME = getAtsContextProperty({
    entity,
    atsTypeId,
    property: 'userName'
  });

  // userGlobalRole
  const VALUE__ATS__USER__GLOBAL_ROLE = getAtsContextProperty({
    entity,
    atsTypeId,
    property: 'userGlobalRole'
  });

  // YES sources
  const YES__ATS__SOURCES = YES(
    Arr(state.sources).length
  );

  const fetchAtsSources = async (event) => {
    await updateState({
      busySources: true
    });
    const sources = Arr(
      await Ashby.getSources({ apiKey: VALUE__ATS__API_KEY })
    ).map(
      (source) => ({
        ...source,
        id: source.id,
        label: `${source.title} (${source.sourceType.title})`,
        title: ''
      })
    );
    await updateState({
      busySources: false,
      sources: sources
    });
    const defaultSourceId = Arr(sources).find((source) => /10x10/i.test(source.label))?.id;
    const currentSourceId = getAtsContextProperty({
      entity: entityRef.current,
      atsTypeId,
      property: 'sourceId'
    });
    if (NOT(currentSourceId)) {
      await onChange(
        getAtsContextUpdated({
          entity: entityRef.current,
          atsTypeId,
          update: {
            sourceId: defaultSourceId
          }
        })
      );
    }
  }

  const fetchAtsUser = async () => {
    await updateState({
      busyUser: true
    });
    // apiKey
    const VALUE__ATS__API_KEY = getAtsContextProperty({
      entity: entityRef.current,
      atsTypeId,
      property: 'apiKey'
    });
    const user = Obj(
      await Ashby.searchUser({
        apiKey: VALUE__ATS__API_KEY,
        userEmail: VALUE__ATS__USER__EMAIL,
      })
    );
    await updateState({
      busyUser: false
    });
    await onChange(
      getAtsContextUpdated({
        entity: entityRef.current,
        atsTypeId,
        update: {
          userId: user.id,
          userName: getPersonName(user),
          userGlobalRole: user.globalRole
        }
      })
    );
  }

  function onChangeTextField(field, callback = Fun) {
    return async (event, value) => {
      const prevValue = getAtsContextProperty({
        entity,
        atsTypeId,
        field
      });
      await onChange(
        getAtsContextUpdated({
          entity,
          atsTypeId,
          update: {
            [field]: value
          }
        })
      );
      if (value === prevValue) { return; }
      debounce(callback)({ prevValue, value });
    }
  }

  const LOADER = <CircularProgress className='icon16 mr-1' />;

  return (
    <Box column flex1 {...props}>

      <Box row>
        <Fieldset
          title='API key'
          subtitle='Authentication credential to ATS.'
        >
          <Box row>
            <TextField
              name="_atsApiKey"
              value={VALUE__ATS__API_KEY}
              onChange={onChangeTextField('apiKey', fetchAtsUser)}
            />
          </Box>
        </Fieldset>
      </Box>

      <Box row
        className='flex-align-left-top'
      >
        <Fieldset
          title='Source'
          subtitle='Select a source from the list'
          nextToTitle={state.busySources && LOADER}
        >
          <Once
            acl={
              VALUE__ATS__API_KEY &&
              !YES__ATS__SOURCES
            }
            promise={fetchAtsSources}
            render={false}
          />
          <Menu dropdown
            name="ashbySourceId"
            value={VALUE__ATS__SOURCE_ID}
            onChange={(value) => {
              onChange(
                getAtsContextUpdated({
                  entity,
                  atsTypeId,
                  update: {
                    sourceId: value
                  }
                })
              );
            }}
            options={Arr(
              state.sources
            )}
            onClick={fetchAtsSources}
          />
        </Fieldset>
        <Fieldset
          title='User'
          nextToTitle={state.busyUser && LOADER}
          subtitle={
            <Box column flex1>
              <Box row flex1 noWrap>
                <Box row className='f-sm fw-400 mr-1'
                  acl={VALUE__ATS__USER__ID}
                >
                  {VALUE__ATS__USER__NAME}&nbsp;
                  ({VALUE__ATS__USER__GLOBAL_ROLE})
                </Box>
              </Box>
              <Box row noWrap flex1>
                <Box row className='f-sm fw-400 mr-1'>
                  {VALUE__ATS__USER__EMAIL}
                </Box>
                <Icon
                  icon='verified'
                  className={
                    joinClassName([
                      'icon18 m-0',
                      VALUE__ATS__USER__ID ? 'c-green' : 'c-red'
                    ])
                  }
                />
              </Box>
            </Box>
          }
        >
          <Once
            acl={VALUE__ATS__API_KEY}
            promise={fetchAtsUser}
            render={false}
          />
        </Fieldset>
      </Box>

    </Box>
  );
}
